/**
 * chart-palette — single source of truth for every chart colour used in the
 * dashboard.
 *
 * 2026-04-24 rewrite (Playwright-audited against the dark theme, near-black
 * background #111827). Prior palette mixed 500–700 weight Tailwind hex that
 * dulled against the dark card surface (green-600 tired, green-300 acidic,
 * blue-600 darker than the theme primary) — see /tmp/palette-audit for the
 * before-shots and per-chart ink dump.
 *
 * Design rules:
 *
 *   1. THEME-LINKED keys pull from `--theme-color-*` CSS variables the
 *      active theme writes at :root (see themes/index.ts → applyTheme). So
 *      hero/positive/negative/warning/muted automatically follow whichever
 *      theme the app is on, and any future theme swap adapts every chart
 *      without touching this file.
 *
 *   2. FALLBACK hex after the CSS var is a dark-appropriate 400-level
 *      Tailwind value. Used on the briefest pre-`applyTheme` paint and as a
 *      safety net if a theme forgets to set a token.
 *
 *   3. DERIVED semantic ramps (rating 1→5, complexity trivial→expert,
 *      latency fast/slow/degraded, failure top-N) stay as literal 400-level
 *      hex. These scales need consistent perceptual steps that don't fit
 *      cleanly on a 5-token theme palette.
 *
 *   4. DARK-FIRST values. 400-level Tailwind weights sit in the perceptual
 *      sweet spot against a ~10–20% lightness background: saturated enough
 *      to read, not so bright they vibrate. 500–700 look muddy; 300 and
 *      below feel neon.
 *
 *   5. ROLE-SPLIT charts (RM vs Non-RM) use hero + NEUTRAL, not hero +
 *      hero-tint. Two close greens on Time-saved were illegible; now RM
 *      gets the hero emerald and Non-RM gets theme-secondary slate — same
 *      pattern already used on the Requests chart for consistency.
 */

// Centralising the var() string keeps the fallback hex in one place. If a
// consumer ever needs the raw hex (rare — tests, SSR), read `themes/default.ts`.
const v = (name: string, fallback: string): string => `var(${name}, ${fallback})`;

export const chartPalette = {
  // ---- identity / default ------------------------------------------------
  /** Primary hero — RM on volume charts, DAU, default bars. Theme blue-400. */
  primary: v('--theme-color-primary', '#60a5fa'),
  /** Background / secondary actor (Non-RM, previous-period). Theme slate-400. */
  muted: v('--theme-color-secondary', '#94a3b8'),
  /** Softer muted for "previous-period" compare series. slate-300. */
  mutedSoft: '#cbd5e1',

  // ---- semantic ----------------------------------------------------------
  /** Wins: time saved, high ratings, fast latency. Theme green-400. */
  positive: v('--theme-color-success', '#4ade80'),
  /** Lighter positive for gradient stops / soft fills — green-500, saturated
   *  enough to carry "new users" / "sent" without looking washed out. User
   *  pass-1 rejected green-300 as too neon for Users-trend and Delivery-trend. */
  positiveSoft: '#22c55e',
  /** Losses: errors, degraded latency, 1★. Theme red-400. */
  negative: v('--theme-color-danger', '#f87171'),
  /** Caveats / threshold crossed (3★, slow-but-ok latency). Theme amber-400. */
  warning: v('--theme-color-warning', '#fbbf24'),
  /** Middle-ground emphasis (3★ alt). amber-300. */
  warningSoft: '#fcd34d',
  /** Companion line (Active-users on Requests) — amber-500 per user direction;
   *  stays orthogonal to the primary blue and reads as "attention" overlay. */
  companion: '#f59e0b',

  // ---- role-split (volume charts: Requests, Cost, etc.) ------------------
  /** RM on neutral charts — blue-600, carries hero weight on the Requests
   *  chart against the amber Active-users line (kept after user feedback
   *  that the 400-weight looked washed against the secondary). */
  rmRequests: '#2563eb',
  /** Non-RM — theme slate-400, reads as "other / background". */
  nonRmRequests: v('--theme-color-secondary', '#94a3b8'),

  // ---- role-split (positive-outcome charts: Time saved) ------------------
  // 2026-04-24: user now wants Time saved timeline to read identically to
  // Requests & Active Users — same blue-600 / slate-400 role split. Keeps
  // the role-split vocabulary consistent across volume and positive-outcome
  // timelines; the "this is a win" story is carried by the KPI delta and
  // the total in the header, not by a bespoke green palette.
  /** RM time-saved bars — blue-600, matches rmRequests. */
  rmTimeSaved: '#2563eb',
  /** Non-RM time-saved bars — theme slate-400, matches nonRmRequests. */
  nonRmTimeSaved: v('--theme-color-secondary', '#94a3b8'),

  // ---- RM brand green (Quality tab RM badge + Rating distribution) -------
  // Same green-600 the <Badge className="bg-green-600">RM</Badge> ships on
  // QualityPage. Reused for Rating distribution (donut + histogram) and the
  // Users-trend "Returning" bar so all RM-coded visuals speak the same green.
  rmBadge: '#16a34a',

  // ---- companion lines / areas -------------------------------------------
  /** "Active users" line on the Requests chart — amber-500, per user
   *  direction. Warm complement to RM-blue, doesn't collide with semantic
   *  red/green. */
  activeUsers: '#f59e0b',
  /** DAU area fill — primary blue. */
  dau: v('--theme-color-primary', '#60a5fa'),

  // ---- rating 1→5 diverging scale ----------------------------------------
  // Five tonal steps across the red-orange-amber-lime-green arc, all at
  // 400 weight so the ramp reads cleanly on dark bg. 1★ matches theme
  // danger, 3★ matches theme warning, 5★ matches theme success — so the
  // ramp anchors to the theme on its extremes and its midpoint.
  rating: {
    1: '#f87171', // red-400 (theme danger)
    2: '#fb923c', // orange-400
    3: '#fbbf24', // amber-400 (theme warning)
    4: '#a3e635', // lime-400
    5: '#4ade80', // green-400 (theme success)
  } as const,

  // ---- latency bands -----------------------------------------------------
  // Mirrors rating 1 / 3 / 5 stops so "slow latency" and "bad rating" speak
  // the same colour vocabulary.
  latency: {
    fast: '#4ade80', // green-400
    slow: '#fbbf24', // amber-400
    degraded: '#f87171', // red-400
  } as const,

  // ---- complexity ramp (Operations) --------------------------------------
  // Same 5-step arc as ratings but inverted semantics: trivial = green
  // (easy = good), expert = red (hard = expensive). User preferred a deeper,
  // more saturated step-out over the 400-level ramp — on Operations the
  // distribution bar tells an "escalating cost" story, so richer hues make
  // the ramp's monotonicity more obvious than thin pastel steps.
  complexity: {
    trivial: '#15803d', // green-700  (cheap / trivial)
    simple: '#22c55e', // green-500
    medium: '#eab308', // yellow-500
    complex: '#f97316', // orange-500
    expert: '#dc2626', // red-600    (expensive / expert)
  } as const,

  // ---- briefs vs reactive (UserDrilldown + Briefs pages) -----------------
  /** Scheduled briefs — theme warning (amber-400), distinct from primary blue. */
  briefs: v('--theme-color-warning', '#fbbf24'),
  /** Reactive (user-triggered) — theme primary, matches RM on volume charts. */
  reactive: v('--theme-color-primary', '#60a5fa'),

  // ---- cost / financial --------------------------------------------------
  /** Cost on the Operations trend — theme accent (violet-400). Stays out of
   *  the wins/losses semantic space and matches the theme accent token. */
  cost: v('--theme-color-accent', '#a78bfa'),

  // ---- failure sequential (BriefsPage failure-reasons histogram) ---------
  // 7-step ranking ramp: warm 400-level row for the top failures fading to
  // muted slate for the tail. Hues chosen so no two adjacent swatches sit
  // on the same H ± 15° band — stays legible at bar-label size.
  failurePalette: [
    '#f87171', // red-400 — most frequent
    '#fb923c', // orange-400
    '#fbbf24', // amber-400
    '#facc15', // yellow-400
    '#f472b6', // pink-400
    '#c084fc', // purple-400
    '#94a3b8', // slate-400 — "Other" / tail
  ] as const,
} as const;

/**
 * Helper for the rating bar-chart — looks up the diverging colour by the
 * numeric rating. Accepts `null` so a caller can render an empty-bucket bar
 * in the neutral muted colour (not all callsites know at render time
 * whether a bucket is missing).
 */
export function ratingColor(rating: 1 | 2 | 3 | 4 | 5 | null): string {
  if (rating == null) return chartPalette.muted;
  return chartPalette.rating[rating];
}
